import { getCurrency } from "@repo/utils";

/**
 * Formats price with appropriate currency symbol, taking into account
 * the position of the currency symbol (before or after the price)
 *
 * @param {number} amountInCents - Amount in cents/pennies
 * @param {string} currencyCode - Currency code (e.g., "USD", "PLN", "EUR")
 * @returns {string} - Formatted price with currency
 *
 * @example
 * formatPriceWithCurrency(1234, "USD") // "$12.34"
 * formatPriceWithCurrency(1234, "PLN") // "12.34 PLN"
 * formatPriceWithCurrency(1234, "EUR") // "12.34 €"
 */
export const formatPriceWithCurrency = (amountInCents, currencyCode) => {
  const currencyObj = getCurrency(currencyCode?.toUpperCase());
  const formattedAmount = (amountInCents / 100).toFixed(2);

  if (currencyObj?.symbolStart) {
    return `${currencyObj.symbolStart}${formattedAmount}`;
  } else if (currencyObj?.symbolEnd) {
    return `${formattedAmount} ${currencyObj.symbolEnd}`;
  }

  // Fallback - use currency code after the price
  return `${formattedAmount} ${currencyCode?.toUpperCase() || ""}`;
};

/**
 * Formats price (already in dollars/euros, not in cents) with appropriate currency symbol,
 * taking into account the position of the currency symbol (before or after the price)
 *
 * @param {number} amount - Amount in dollars/euros (e.g., 15.99)
 * @param {string|object} currencyCodeOrObj - Currency code (e.g., "USD") or currency object from getCurrency
 * @param {number} decimals - Number of decimal places (default 2)
 * @returns {string} - Formatted price with currency
 *
 * @example
 * formatPrice(15.99, "USD") // "$15.99"
 * formatPrice(15, "PLN", 0) // "15 PLN"
 * formatPrice(15.5, currencyObj) // uses object from getCurrency
 */
export const formatPrice = (amount, currencyCodeOrObj, decimals = 2) => {
  const currencyObj = typeof currencyCodeOrObj === 'string'
    ? getCurrency(currencyCodeOrObj?.toUpperCase())
    : currencyCodeOrObj;


  const formattedAmount = decimals >= 0
    ? (amount || 0).toFixed(decimals)
    : amount || 0;

  if (currencyObj?.symbolStart) {
    return `${currencyObj.symbolStart}${formattedAmount}`;
  } else if (currencyObj?.symbolEnd) {
    return `${formattedAmount} ${currencyObj.symbolEnd}`;
  }

  // Fallback - use currency code after the price
  const code = typeof currencyCodeOrObj === 'string'
    ? currencyCodeOrObj?.toUpperCase()
    : currencyObj?.code?.toUpperCase();
  return `${formattedAmount} ${code || ""}`;
};

